import React from "react";
import { I18n } from 'react-redux-i18n';

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import { getCommonFieldBasedElementParams, useDatasheetElementController } from "@modules/datasheets/components/result/controllers/datasheet-element";
import { trDts } from "@modules/datasheets/utils/i18n";
import { TableFieldDataItem, TableFieldRowServerShape, TableFieldSubfieldDataDto } from "./table-field.types";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";
import { addDatasheetTableRow, deleteDatasheetTableRow, getTableDatasheetFormats, updateFieldValue } from "@modules/datasheets/services/datasheet";
import { useMessage } from "@common/hooks/message";
import { FieldType } from "@common/dto/field/type";
import { TABLE_VALID_DATE_FORMATS, parseDateString, isValidDate, formatDateAsApi } from "@modules/datasheets/utils/date-validation";
import { InfoMessage } from "@common/components/info-message";
import { executeUI, refreshPedigreeSubtextAndSymbols } from "@common/utils/ui-bridge";
import { useDatasheetResultFieldEditable } from "../../context";

export type TableFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto> & {
  context?: number;
  globalId?: number;
  /** Parent datasheet format id (for opening table datasheet result in new tab). */
  parentFormatId?: number;
  /** Parent datasheet current tab id (for opening table datasheet result in new tab). */
  parentTabId?: number;
  /** When true, datasheet is in pedigree split view; refresh subtext/symbols after updateValue */
  formatOnly?: boolean;
};

export const useTableFieldController = (props: TableFieldProps) => {
  const { datasheetElement, onChange } = props;
  const [selectedItemId, setSelectedItemId] = React.useState<string | undefined>();
  const { showConfirmMessage, showMessage, closeMessage, messageProps } = useMessage();

  const {
    headingSizeDependencies,
    headingPlacedHorizontally,
    headingPosition
  } = getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(
    props,
    { headingSizeDependencies }
  );

  const isEditable = useDatasheetResultFieldEditable(Boolean(datasheetElement.original.editable));
  const tableId = datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id;
  const systemField = datasheetElement.original.field.systemField ?? false;
  // Default TDS format id for this table field, provided by loadDSFormat (IDS).
  // Support a few legacy key variants defensively.
  const defaultTableDatasheetFormatId: number | undefined = React.useMemo(() => {
    const originalAny = datasheetElement.original as any;
    const fieldAny = datasheetElement.original.field as any;
    const raw =
      originalAny?.defaultTableDatasheetFormatId ??
      fieldAny?.defaultTableDatasheetFormatId ??
      originalAny?.defaulttableDatasheetFormatId ??
      fieldAny?.defaulttableDatasheetFormatId ??
      originalAny?.defaulttabledatasheetformatid ??
      fieldAny?.defaulttabledatasheetformatid ??
      undefined;
    const parsed = raw != null ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [datasheetElement.original]);

  const resolvedSubfields = React.useMemo(() => {
    const dsSubfields = datasheetElement.original.dssubfields || datasheetElement.original.m_ds_subfields;
    if (dsSubfields && dsSubfields.length > 0) {
      return dsSubfields
        .map((subfield) => {
          return (
            subfield.field ||
            datasheetElement.original.field.subfields?.find((field) => field.fieldid === subfield.field?.fieldid)
          );
        })
        .filter((field): field is DatasheetFieldComponentOriginFieldGetDto => Boolean(field));
    }
    return datasheetElement.original.field.subfields || [];
  }, [datasheetElement.original.dssubfields, datasheetElement.original.m_ds_subfields, datasheetElement.original.field.subfields]);

  // Get tableData from merged datasheet-data response (attached under field.tableData)
  const tableDataFromResponse: Array<TableFieldRowServerShape> = React.useMemo(() => {
    const tableData = (datasheetElement.original.field as any)?.tableData;
    return Array.isArray(tableData) ? tableData : [];
  }, [datasheetElement.original.field]);

  const currentValue: Array<TableFieldDataItem> = React.useMemo(() => {
    return tableDataFromResponse.map((row) => {
      const cells: Record<number, TableFieldSubfieldDataDto> = {};
      if (Array.isArray(row.data)) {
        row.data.forEach((d) => {
          if (d && typeof d.fieldId === 'number') {
            cells[d.fieldId] = d;
          }
        });
      }
      return {
        id: String(row.rrn),
        rrn: row.rrn,
        cells,
      };
    });
  }, [tableDataFromResponse]);

  const updateTableData = React.useCallback((nextTableData: Array<TableFieldRowServerShape>) => {
    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        field: {
          ...datasheetElement.original.field,
          tableData: nextTableData,
        } as any,
      },
    });
  }, [onChange, datasheetElement]);

  const updateField = React.useCallback((field: Partial<DatasheetFieldComponentOriginFieldGetDto>) => {
    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        field: {
          ...datasheetElement.original.field,
          ...field,
        },
      },
    });
  }, [onChange, datasheetElement]);

  const markTableSyncDirty = React.useCallback(() => {
    if (typeof sessionStorage === "undefined" || props.globalId == null) {
      return;
    }
    sessionStorage.setItem(`DS_TABLE_SYNC_MARKER_${props.globalId}`, String(Date.now()));
  }, [props.globalId]);

  /** Passthrough: keep row order as received from BE. No sort, no dedupe. */
  const getRrnsPassthrough = React.useCallback((rows: Array<TableFieldRowServerShape>): number[] => {
    return rows
      .map((row) => Number(row?.rrn))
      .filter((rrn) => Number.isFinite(rrn) && rrn > 0);
  }, []);

  const updatePendingTdsRows = React.useCallback((rows: Array<TableFieldRowServerShape>) => {
    if (typeof sessionStorage === "undefined" || props.globalId == null) {
      return;
    }
    const rrns = getRrnsPassthrough(rows);
    sessionStorage.setItem(
      `TDS_PENDING_ROW_RRNS_${tableId}_${props.globalId}`,
      JSON.stringify(rrns)
    );
  }, [props.globalId, tableId, getRrnsPassthrough]);

  const handleResizeColumn = React.useCallback(({ field }: { field: DatasheetFieldComponentOriginFieldGetDto }) => {
    const existingDsSubfields =
      datasheetElement.original.dssubfields ||
      datasheetElement.original.m_ds_subfields ||
      [];
    const nextDsSubfields = (existingDsSubfields.length > 0
      ? existingDsSubfields
      : resolvedSubfields.map((subField) => ({
        id: subField.fieldid,
        field: subField,
        width: subField.defaultWidth || 100,
        height: 0,
        x: 0,
        y: 0,
      })) as Array<DatasheetFieldComponentGetDto>
    ).map((subField) =>
      subField.field?.fieldid === field.fieldid
        ? {
          ...subField,
          width: (field as any).width ?? subField.width,
          field,
        }
        : subField
    );

    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        dssubfields: nextDsSubfields,
        m_ds_subfields: nextDsSubfields,
      },
    });
  }, [datasheetElement, onChange, resolvedSubfields]);

  const handleRowClick = React.useCallback((item: TableFieldDataItem) => {
    const itemId = String(item.rrn);
    setSelectedItemId(itemId);
  }, []);

  const handleItemAdd = React.useCallback(async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!isEditable || props.globalId == null || props.context == null) {
      return;
    }

    try {
      const row = await addDatasheetTableRow({
        globalId: props.globalId,
        context: props.context,
        tableId,
        systemField,
        copyFromRrn: -1,
      });

      if (row?.rrn == null) {
        showMessage?.({
          header: 'Error',
          text: 'Failed to add row. Please try again.',
          size: 'small',
        });
        return;
      }

      // Ensure the response structure matches what we expect
      const newRow: TableFieldRowServerShape = {
        rrn: row.rrn,
        data: Array.isArray(row.data) ? row.data : [],
      };
      const next = [...tableDataFromResponse, newRow];
      updateTableData(next);
      markTableSyncDirty();
      updatePendingTdsRows(next);
      // Select the newly added row
      const newRowId = String(row.rrn);
      setSelectedItemId(newRowId);
    } catch (error) {
      showMessage?.({
        header: 'Error',
        text: 'Failed to add row. Please try again.',
        size: 'small',
      });
    }
  }, [isEditable, props.globalId, props.context, tableId, systemField, tableDataFromResponse, updateTableData, showMessage, markTableSyncDirty, updatePendingTdsRows]);

  const handleItemCopy = React.useCallback(async (e?: React.MouseEvent) => {
    // Prevent event propagation to avoid triggering row clicks
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!isEditable || props.globalId == null || props.context == null) {
      return;
    }

    if (!selectedItemId) {
      showMessage?.({
        header: 'Information',
        text: 'Please select a row to copy.',
        size: 'small',
      });
      return;
    }

    const rrn = parseInt(selectedItemId, 10);
    if (Number.isNaN(rrn) || rrn <= 0) {
      showMessage?.({
        header: 'Error',
        text: 'Invalid row selection. Please select a row and try again.',
        size: 'small',
      });
      return;
    }

    try {
      const row = await addDatasheetTableRow({
        globalId: props.globalId,
        context: props.context,
        tableId,
        systemField,
        copyFromRrn: rrn,
      });

      if (row?.rrn == null) {
        showMessage?.({
          header: 'Error',
          text: 'Failed to copy row. Please try again.',
          size: 'small',
        });
        return;
      }

      const newRow: TableFieldRowServerShape = {
        rrn: row.rrn,
        data: Array.isArray(row.data) ? row.data : [],
      };
      const next = [...tableDataFromResponse, newRow];
      updateTableData(next);
      markTableSyncDirty();
      updatePendingTdsRows(next);
      // Select the newly copied row
      const newRowId = String(row.rrn);
      setSelectedItemId(newRowId);
    } catch (error) {
      showMessage?.({
        header: 'Error',
        text: 'Failed to copy row. Please try again.',
        size: 'small',
      });
    }
  }, [isEditable, props.globalId, props.context, selectedItemId, currentValue, tableId, systemField, tableDataFromResponse, updateTableData, showMessage, markTableSyncDirty, updatePendingTdsRows]);

  const handleItemDelete = React.useCallback(async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!isEditable || props.globalId == null || props.context == null) {
      return;
    }

    if (!selectedItemId) {
      showMessage?.({
        header: 'Information',
        text: 'Please select a row to delete.',
        size: 'small',
      });
      return;
    }

    const rrn = parseInt(selectedItemId, 10);
    if (Number.isNaN(rrn) || rrn <= 0) {
      showMessage?.({
        header: 'Error',
        text: 'Invalid row selection. Please select a row and try again.',
        size: 'small',
      });
      return;
    }
    showConfirmMessage({
      header: trDts('Confirmation Message'),
      content: trDts('Are you sure you want to delete this row?'),
      confirmText: trDts('Delete'),
      cancelText: trDts('Cancel'),
      onCustomOk: async () => {
        try {
          await deleteDatasheetTableRow({
            globalId: props.globalId!,
            context: props.context!,
            tableId,
            systemField,
            rrn,
          });
          const next = tableDataFromResponse.filter((r) => r.rrn !== rrn);
          updateTableData(next);
          markTableSyncDirty();
          updatePendingTdsRows(next);
          // Clear selection after deletion
          setSelectedItemId(undefined);
        } catch (error) {
          showMessage?.({
            header: 'Error',
            text: 'Failed to delete row. Please try again.',
            size: 'small',
          });
        }
      },
      size: 'small',
    });
  }, [isEditable, props.globalId, props.context, selectedItemId, currentValue, showConfirmMessage, showMessage, tableId, systemField, tableDataFromResponse, updateTableData, markTableSyncDirty, updatePendingTdsRows]);

  const handleOpenTableDatasheet = React.useCallback(async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!selectedItemId) {
      showMessage?.({
        header: 'Information',
        text: 'Please select a row to open table datasheet.',
        size: 'small',
      });
      return;
    }

    if (props.globalId == null || props.context == null) {
      return;
    }

    const explicitDefaultFormatId =
      defaultTableDatasheetFormatId != null && defaultTableDatasheetFormatId > 0
        ? defaultTableDatasheetFormatId
        : undefined;

    if (!explicitDefaultFormatId) {
      try {
        const tdsFormats = await getTableDatasheetFormats({
          context: props.context,
          tableFieldId: tableId,
        });
        if (!tdsFormats || tdsFormats.length === 0) {
          showMessage?.({
            header: I18n.t('Information'),
            text: I18n.t('No table datasheet format is available for this table.'),
            size: 'small',
          });
          return;
        }
      } catch (_error) {
        showMessage?.({
          header: I18n.t('Information'),
          text: I18n.t('No table datasheet format is available for this table.'),
          size: 'small',
        });
        return;
      }
    }

    const tabTitle = datasheetElement.original.field.fieldname || "Table Datasheet";
    // Table datasheet result tab must use context=8 (TDS). Pass parent context as tableContext (args[6]). Pass selected row rrn (args[7]) so the new tab opens to that row.
    const TABLE_DS_CONTEXT = 8;
    const parentFormatIdForBridge = explicitDefaultFormatId ?? 0;
    const args = [
      tabTitle,
      String(TABLE_DS_CONTEXT),
      String(parentFormatIdForBridge),
      String(props.parentTabId ?? 0),
      String(tableId),
      String(props.globalId ?? 0),
      String(props.context ?? 0),
      String(selectedItemId ?? 0),
    ];
    // Store TDS format id and parent tab id so the new tab can use them if
    // Java passes 0 (workaround until bridge passes it through).
    if (typeof sessionStorage !== "undefined") {
      const formatIdForStorage = explicitDefaultFormatId;

      if (formatIdForStorage && formatIdForStorage > 0) {
        sessionStorage.setItem(
          `TDS_PENDING_FORMAT_ID_${tableId}_${props.globalId ?? 0}`,
          String(formatIdForStorage)
        );
      }

      const parentTabIdForStorage = props.parentTabId ?? 0;
      sessionStorage.setItem(
        `TDS_PENDING_TAB_ID_${tableId}_${props.globalId ?? 0}`,
        String(parentTabIdForStorage > 0 ? parentTabIdForStorage : 1)
      );
      const rowRrns = getRrnsPassthrough(tableDataFromResponse);
      sessionStorage.setItem(
        `TDS_PENDING_ROW_RRNS_${tableId}_${props.globalId ?? 0}`,
        JSON.stringify(rowRrns)
      );
    }

    try {
      await executeUI("DATASHEETS", "RESULT", ...args);
    } catch (_error) {
      showMessage?.({
        header: 'Error',
        text: 'Failed to open table datasheet. Please try again.',
        size: 'small',
      });
    }
  }, [selectedItemId, showMessage, props.globalId, props.context, props.parentFormatId, props.parentTabId, datasheetElement.original.field.fieldname, tableId, defaultTableDatasheetFormatId, tableDataFromResponse, getRrnsPassthrough]);

  //new changes for date field validation 
  const handleCellValueChange = React.useCallback(async (rowRrn: number, subfield: DatasheetFieldComponentOriginFieldGetDto, value: any) => {
    if (!isEditable || props.globalId == null || props.context == null) return;
    const fieldId = subfield.fieldid;
    const subfieldSystem = subfield.systemField ?? false;

    // Date validation (manual typed blur)
    if (subfield.fieldtype?.typeid === FieldType.date) {
      if (value == null || value === '') {
        // allow clear
      } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        // Already normalized (YYYY-MM-DD) - allow
      } else if (typeof value === 'string' && !isValidDate(value)) {
        //new change
        const content = React.createElement(
          InfoMessage,
          null,
          'Invalid date format. Dates must be entered as:',
          React.createElement(
            'ul',
            { style: { marginTop: 4, paddingLeft: 20 } },
            TABLE_VALID_DATE_FORMATS.map(format =>
              React.createElement('li', { key: format.value }, format.label)
            )
          )
        );

        showMessage?.({
          header: 'Information Message',
          content,
          size: 'small',
        });
        return;
      } else if (typeof value === 'string') {
        const parsed = parseDateString(value);
        if (parsed) {
          value = formatDateAsApi(parsed);
        }
      }
    }

    // Normalize checkbox values
    if (subfield.fieldtype?.typeid === FieldType.checkbox) {
      value = value ? 'Y' : 'N';
    }

    const next = tableDataFromResponse.map((r) => {
      if (r.rrn !== rowRrn) return r;
      const nextData = Array.isArray(r.data) ? [...r.data] : [];
      const idx = nextData.findIndex((d: any) => d?.fieldId === fieldId);
      const nextCell: any = idx >= 0 ? { ...nextData[idx] } : { fieldId, system: subfieldSystem, context: props.context, globalId: props.globalId, table: false };
      nextCell.dataValue = value;
      nextCell.rawValue = value;
      nextCell.displayValue = String(value ?? '');
      if (idx >= 0) {
        nextData[idx] = nextCell;
      } else {
        nextData.push(nextCell);
      }
      return { ...r, data: nextData };
    });
    updateTableData(next);
    markTableSyncDirty();

    try {
      const response = await updateFieldValue({
        globalId: props.globalId,
        context: props.context,
        fieldId,
        systemField: subfieldSystem,
        ds: true,
        value: value ?? '',
        rrn: rowRrn,
        tableId,
      });
      if (response?.code && response.code !== 200) {
        const errors = response.errors ?? [];
        const errorMessage = errors.join('\n') || trDts('Validation failed');
        showMessage?.({
          header: trDts('Validation Error'),
          content: errorMessage,
          confirmText: trDts('OK'),
        });
        return;
      }
      if (props.formatOnly) {
        refreshPedigreeSubtextAndSymbols();
      }
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to update table cell value', e);
    }
  }, [isEditable, props.globalId, props.context, props.formatOnly, tableId, tableDataFromResponse, updateTableData, showMessage, markTableSyncDirty]);

  const footerActions = React.useMemo(() => {
    const actions = [
      {
        disabled: !isEditable,
        label: trDts('Add'),
        onClick: handleItemAdd,
        icon: {
          enabled: 'gif/row_add.gif',
          disabled: 'gif/row_add_inactive.gif',
        },
      },
      {
        disabled: !isEditable || !selectedItemId,
        label: trDts('Copy'),
        onClick: handleItemCopy,
        icon: {
          enabled: 'gif/row_duplicate.gif',
          disabled: 'gif/row_duplicate_inactive.gif',
        },
      },
      {
        disabled: !isEditable || !selectedItemId,
        label: trDts('Delete'),
        onClick: handleItemDelete,
        icon: {
          enabled: 'gif/row_delete.gif',
          disabled: 'gif/row_delete_inactive.gif',
        },
      },
    ];

    // Show "Open Table Datasheet" only when a row is selected.
    if (selectedItemId) {
      actions.push({
        disabled: false,
        label: trDts('Open Table Datasheet'),
        onClick: handleOpenTableDatasheet,
        icon: {
          enabled: 'gif/ds_table.gif',
          disabled: 'gif/ds_table.gif',
        },
      });
    }

    return actions;
  }, [isEditable, selectedItemId, handleItemAdd, handleItemCopy, handleItemDelete, handleOpenTableDatasheet]);

  return {
    currentValue,
    handleRowClick,
    footerActions,
    headingPlacedHorizontally,
    headingPosition,
    handleResizeColumn,
    selectedItemId,
    isEditable,
    handleCellValueChange,
    messageProps,
    closeMessage,
    ...baseDatasheetElementController,
  };
};
